import type { ReactNode } from 'react';

import type { SocialPlatform, SocialPresence } from '../../../../api/venues';

import { ExternalLink } from './ExternalLink';
import { cadenceLabel, formatAudience, PLATFORM_LABELS } from './social-form';
import { socialDisplay } from './social-format';
import { SocialVerifyCell } from './SocialVerifyCell';
import { UnknownValue } from './UnknownValue';

export interface PlatformCells {
  name: ReactNode;
  handle: ReactNode;
  audience: ReactNode;
  cadence: ReactNode;
  verify: ReactNode;
}

const dash = <span style={{ color: 'var(--nb-muted)' }}>—</span>;

interface CellInput {
  platform: SocialPlatform;
  presence: SocialPresence | undefined;
  onVerify?: (platform: SocialPlatform) => void;
  busy: boolean;
  alignEnd: boolean;
}

/** The pieces of one platform's read row, ready to place in either layout. */
export function platformCells({
  platform,
  presence,
  onVerify,
  busy,
  alignEnd,
}: CellInput): PlatformCells {
  const empty = !presence?.handle;
  const link = socialDisplay(platform, presence?.handle);
  const audience = formatAudience(presence?.audienceSize);
  const cadence = cadenceLabel(presence?.cadence);

  const audienceCell = audience ? <b className="nb-tabular">{audience}</b> : <UnknownValue />;
  const cadenceCell = cadence === 'Unknown' ? <UnknownValue /> : cadence;

  return {
    name: (
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--nb-ink)' }}>
        {PLATFORM_LABELS[platform]}
      </div>
    ),
    handle: link ? (
      <ExternalLink mono href={link.href}>
        {link.text}
      </ExternalLink>
    ) : (
      <UnknownValue>No handle</UnknownValue>
    ),
    audience: empty ? dash : audienceCell,
    cadence: empty ? dash : cadenceCell,
    verify: (
      <SocialVerifyCell
        platform={platform}
        verifiedAt={presence?.lastVerifiedAt}
        hasHandle={!empty}
        onVerify={onVerify}
        busy={busy}
        alignEnd={alignEnd}
      />
    ),
  };
}
